'use client';

import React from 'react';

import { Box } from './box';
import type { BoxProps } from './box';

export interface ScrollAreaProps extends BoxProps {
  /**
   * Custom scrollbar styles
   */
  scrollbarWidth?: 'thin' | 'auto' | 'none';
  /**
   * Hide scrollbar on hover
   */
  hideScrollbarOnHover?: boolean;
}

/**
 * ScrollArea component - provides a scrollable area with custom scrollbar styling
 */
export const ScrollArea: React.FC<ScrollAreaProps> = ({
  children,
  scrollbarWidth = 'thin',
  hideScrollbarOnHover = false,
  ...boxProps
}) => {
  const scrollbarStyles = {
    '&::-webkit-scrollbar': {
      width: scrollbarWidth === 'thin' ? '6px' : '12px',
      height: scrollbarWidth === 'thin' ? '6px' : '12px',
      display: scrollbarWidth === 'none' ? 'none' : 'block',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#CBD5E0',
      borderRadius: '3px',
      '&:hover': {
        backgroundColor: '#A0AEC0',
      },
    },
    scrollbarWidth: scrollbarWidth === 'none' ? 'none' : scrollbarWidth,
    scrollbarColor: '#CBD5E0 transparent',
    ...(hideScrollbarOnHover && {
      '&:not(:hover)': {
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
      },
    }),
  };

  return (
    <Box overflowY="auto" overflowX="hidden" css={scrollbarStyles} {...boxProps}>
      {children}
    </Box>
  );
};

ScrollArea.displayName = 'ScrollArea';
